import { useState } from "react";
import "../marketing/landing.css";
import "./login.css";
import { apiFetch } from "../lib/auth";
import { BrandMark } from "../ui/BrandMark";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";

/**
 * `/forgot` — "I can't sign in".
 *
 * WHY IT ONLY EXISTS NOW. `POST /api/auth/request-reset` has been live since June, but its only
 * delivery mechanism was email and no mailserver was configured, so a link to this form would
 * have been a link to a dead end — the request would succeed, nothing would arrive, and the
 * person would be left believing a reset was on its way. That is worse than no link at all. Mail
 * moved to Google Workspace on 2026-09-04, so the form now leads somewhere.
 *
 * THE RESPONSE IS DELIBERATELY THE SAME EITHER WAY. The server answers identically for an
 * address that has an account and one that does not — a different message, or a different
 * timing, is an account-enumeration oracle on an unauthenticated endpoint. So this screen cannot
 * tell the visitor whether their address was found, and does not pretend to: it says what was
 * done, not what was true, and names the fallback that works regardless.
 *
 * No credential and no token is stored here. The address lives in React state for the duration
 * of the submit and nowhere else.
 */
export function ForgotScreen() {
  useDocumentTitle(vbTitle("Reset your password"));
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch("/api/auth/request-reset", {
        method: "POST",
        body: { email: email.trim().toLowerCase() },
        onUnauthorized: () => {
          /* Nobody is signed in here — that is the normal case, not a session expiry. */
        },
      });
      setSent(true);
    } catch {
      // A network or server failure is the ONE thing worth reporting. Anything the server
      // actually answered is the neutral 200 above.
      setError("We could not reach the server. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vb-landing vb-auth">
      <div className="auth-card">
        <a className="auth-mark" href="/">
          <BrandMark height={80} style={{ height: "var(--vb-mark-h, 80px)", width: "auto" }} />
        </a>

        {sent ? (
          <>
            <h1>Check your email</h1>
            <p className="auth-sub">
              If <strong>{email.trim().toLowerCase()}</strong> has a ValuBrief account, a reset
              link is on its way. It works once and expires in 24 hours.
            </p>
            <p className="auth-foot">
              Nothing arrived? Check spam first. If it still has not come, ask your ValuBrief
              administrator to send you a link directly — they can issue one without email.
            </p>
            <p className="auth-foot">
              <a href="/login">Back to sign in</a>
            </p>
          </>
        ) : (
          <>
            <h1>Reset your password</h1>
            <p className="auth-sub">
              Enter the address you sign in with and we will email you a link to choose a new
              password.
            </p>
            <form onSubmit={submit} noValidate>
              <label htmlFor="vb-forgot-email">Email</label>
              <input
                id="vb-forgot-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
              {error ? (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              ) : null}
              <button className="btn" type="submit" disabled={busy || !email.trim()}>
                {busy ? "Sending…" : "Email me a link"}
              </button>
            </form>
            <p className="auth-foot">
              <a href="/login">Back to sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotScreen;
